import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import { History, Filter, Search, Calendar, User, Folder, FileText, X } from 'lucide-react';
import { useState, useMemo } from 'react';
import { format, subDays, parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { AuditReportPreview } from '../components/audit/AuditReportPreview';

const EVENT_TYPES = [
  { value: '', label: 'All Events' },
  { value: 'upload', label: 'Uploads' },
  { value: 'download', label: 'Downloads' },
  { value: 'view', label: 'Views' },
  { value: 'transfer', label: 'Transfers' },
  { value: 'legal_hold', label: 'Legal Holds' },
  { value: 'integrity_check', label: 'Integrity Checks' },
];

const DATE_PRESETS = [
  { value: '', label: 'All Time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom Range' },
];

export default function AuditLog() {
  const { hasRole } = useAuth();
  const [eventType, setEventType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [caseFilter, setCaseFilter] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);

  const { data: events, isLoading } = useQuery({
    queryKey: ['custody-events', eventType],
    queryFn: () => apiClient.getCustodyEvents({ eventType: eventType || undefined }),
  });

  const { data: stats } = useQuery({
    queryKey: ['custody-stats'],
    queryFn: () => apiClient.getCustodyStats(),
  });

  const { data: cases } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  // Get unique users from events
  const uniqueUsers = useMemo(() => {
    if (!events) return [];
    const users = new Map<string, string>();
    events.forEach(e => {
      if (e.performedBy && e.performedByName) {
        users.set(e.performedBy, e.performedByName);
      }
    });
    return Array.from(users.entries()).map(([id, name]) => ({ id, name }));
  }, [events]);

  // Only SystemAdmin and CaseManager can access this page
  if (!hasRole(['SystemAdmin', 'CaseManager'])) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You don't have permission to view the audit log.</p>
      </div>
    );
  }

  // Calculate date range based on preset or custom dates
  const getDateRange = () => {
    if (datePreset === 'custom' && startDate && endDate) {
      return { 
        from: startOfDay(parseISO(startDate)), 
        to: endOfDay(parseISO(endDate)) 
      };
    }
    if (datePreset && datePreset !== 'custom') {
      const days = parseInt(datePreset);
      return { 
        from: startOfDay(subDays(new Date(), days)), 
        to: endOfDay(new Date()) 
      };
    }
    return null;
  };

  const dateRange = getDateRange();

  const filteredEvents = useMemo(() => {
    if (!events) return [];
    
    return events.filter((event) => {
      // Search filter
      const matchesSearch = !searchTerm || 
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.performedByName.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Date range filter
      const matchesDate = !dateRange || isWithinInterval(
        new Date(event.performedAt),
        { start: dateRange.from, end: dateRange.to }
      );
      
      // User filter
      const matchesUser = !userFilter || event.performedBy === userFilter;
      
      // Case filter (would need evidence lookup in real impl)
      const matchesCase = !caseFilter; // Simplified for now
      
      return matchesSearch && matchesDate && matchesUser && matchesCase;
    });
  }, [events, searchTerm, dateRange, userFilter, caseFilter]);

  const activeFiltersCount = [eventType, datePreset, userFilter, caseFilter].filter(Boolean).length;

  const clearFilters = () => {
    setEventType('');
    setDatePreset('');
    setStartDate('');
    setEndDate('');
    setUserFilter('');
    setCaseFilter('');
    setSearchTerm('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-gray-600 mt-1">
            Complete chain of custody audit trail for all evidence
          </p>
        </div>
        <button
          onClick={() => setShowExportModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <FileText className="h-4 w-4" />
          Export Report
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Total Events</p>
            <p className="text-2xl font-bold text-gray-900">{stats.totalEvents}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Uploads</p>
            <p className="text-2xl font-bold text-green-600">{stats.byEventType?.upload ?? 0}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Downloads</p>
            <p className="text-2xl font-bold text-blue-600">{stats.byEventType?.download ?? 0}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Integrity Checks</p>
            <p className="text-2xl font-bold text-purple-600">{stats.byEventType?.integrity_check ?? 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <span className="font-medium text-gray-900">Filters</span>
            {activeFiltersCount > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                {activeFiltersCount} active
              </span>
            )}
          </div>
          {activeFiltersCount > 0 && (
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <X className="h-4 w-4" />
              Clear all
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search audit log..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Event Type */}
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {EVENT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>

          {/* User Filter */}
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Users</option>
              {uniqueUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>

          {/* Case Filter */}
          <div className="relative">
            <Folder className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={caseFilter}
              onChange={(e) => setCaseFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Cases</option>
              {cases?.map((c) => (
                <option key={c.caseNumber} value={c.caseNumber}>
                  {c.caseNumber} - {c.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Date Range Row */}
        <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Date Range:</span>
          </div>
          
          <select
            value={datePreset}
            onChange={(e) => {
              setDatePreset(e.target.value);
              if (e.target.value !== 'custom') {
                setStartDate('');
                setEndDate('');
              }
            }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {DATE_PRESETS.map((preset) => (
              <option key={preset.value} value={preset.value}>
                {preset.label}
              </option>
            ))}
          </select>

          {datePreset === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Events table */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      ) : filteredEvents?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <History className="h-12 w-12 text-gray-400 mx-auto" />
          <p className="text-gray-500 mt-4">No audit events found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEvents?.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(event.performedAt), 'MMM d, yyyy h:mm:ss a')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                          event.eventType === 'upload'
                            ? 'bg-green-100 text-green-800'
                            : event.eventType === 'download'
                            ? 'bg-blue-100 text-blue-800'
                            : event.eventType === 'legal_hold'
                            ? 'bg-amber-100 text-amber-800'
                            : event.eventType === 'integrity_check'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {event.eventType.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                      {event.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {event.performedByName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {event.ipAddress}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Export Report Modal */}
      {showExportModal && filteredEvents && stats && (
        <AuditReportPreview
          isOpen={showExportModal}
          events={filteredEvents}
          filters={{
            dateRange: dateRange ? { from: dateRange.from.toISOString(), to: dateRange.to.toISOString() } : undefined,
            eventType: eventType || undefined,
          }}
          stats={stats}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}
